import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, type PricingRow } from "@/lib/admin-api";

export const Route = createFileRoute("/admin/pricing")({
  component: AdminPricingPage,
});

function relativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface PricingTableRowProps {
  row: PricingRow;
}

function PricingTableRow({ row }: PricingTableRowProps) {
  const queryClient = useQueryClient();
  const [newValue, setNewValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const isCents = row.key.endsWith("_c");
  const inputId = `new-value-${row.key}`;

  async function handleSave() {
    setValidationError(null);

    if (newValue.includes(".")) {
      setValidationError("Must be a whole number");
      return;
    }

    const parsed = parseInt(newValue, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      setValidationError("Must be a non-negative integer");
      return;
    }

    setSaveStatus("saving");
    try {
      await adminApi.updatePricing(row.key, parsed);
      await queryClient.invalidateQueries({ queryKey: ["admin-pricing"] });
      setNewValue("");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
    }
  }

  return (
    <tr className="pricing-table__row">
      <td className="pricing-table__key">
        <code>{row.key}</code>
      </td>
      <td className="pricing-table__value">
        <span>{row.value_int}</span>
        {isCents && (
          <span className="pricing-table__cents">{`$${(row.value_int / 100).toFixed(2)}`}</span>
        )}
      </td>
      <td className="pricing-table__meta">
        <span className="pricing-table__time">{relativeTime(row.updated_at)}</span>
        {row.updated_by && <span className="pricing-table__by">{row.updated_by}</span>}
      </td>
      <td className="pricing-table__edit">
        <label htmlFor={inputId} className="sr-only">
          New value for {row.key}
        </label>
        <input
          id={inputId}
          type="number"
          min={0}
          step={1}
          value={newValue}
          onChange={(e) => {
            setNewValue(e.target.value);
            setValidationError(null);
            if (saveStatus !== "saving") setSaveStatus("idle");
          }}
          className="pricing-table__input"
          placeholder="New value"
          disabled={saveStatus === "saving"}
        />
        <button
          onClick={() => void handleSave()}
          disabled={saveStatus === "saving" || !newValue}
          className="pricing-table__save-btn"
        >
          {saveStatus === "saving" ? "Saving…" : "Save"}
        </button>
        {validationError && (
          <p className="pricing-table__validation-error" role="alert">
            {validationError}
          </p>
        )}
        {saveStatus === "saved" && (
          <p className="pricing-table__saved" aria-live="polite">
            Saved
          </p>
        )}
        {saveStatus === "error" && (
          <p className="pricing-table__save-error" role="alert">
            Save failed
          </p>
        )}
      </td>
    </tr>
  );
}

function AdminPricingPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-pricing"],
    queryFn: () => adminApi.listPricing(),
  });

  if (isLoading) {
    return (
      <div className="admin-page" data-testid="admin-pricing">
        <p>Loading…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="admin-page" data-testid="admin-pricing">
        <p role="alert">Failed to load pricing data.</p>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-pricing">
      <h1 className="admin-page__heading">Pricing</h1>
      <table className="pricing-table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Current Value</th>
            <th>Last Edited</th>
            <th>Update</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <PricingTableRow key={row.key} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
